import moment from 'moment';
import { buildCommentAction } from '../comments/Comment.js';
import { getWordWithCorrectEnding, viewDocumentFile } from '../helpers/HelperFunctions.js';
import { alfrescoTypes, attachmentTypes, certificationKinds, vacationKinds, formFieldTypes, vacationMinLength, settingsValueFieldTypes, tripDestinations, transportationMethods } from '../../constants.js';
import { messageDialog } from '../ModalDialog.js';
import i18next from "../../i18n.js";

export async function getComents(docId) {
    return await buildCommentAction('GET', `/api/comments/get/${docId}`);
}
export async function addComment(comment, docId) {
    await buildCommentAction('POST', '/api/comments/crud', {
        action: 'addComment',
        content: comment.content,
        documentNodeRef: docId,
    });
}

export const getLimitDays = (documentData, limitsInfo) => {
    let leftDays = null;
    if (limitsInfo && documentData.properties) {
        switch (documentData.properties['sx:dfDocKind']) {
            case vacationKinds.main:
                leftDays = parseInt(limitsInfo.limits.unusedVacationDaysMain);
                break;
            case vacationKinds.ubd:
                leftDays = parseInt(limitsInfo.limits.unusedVacationDaysUBD);
                break;
            case vacationKinds.additionalChildcare:
                leftDays = parseInt(limitsInfo.limits.unusedVacationDaysChild);
                break;
            case vacationKinds.chaes:
                leftDays = parseInt(limitsInfo.limits.unusedVacationDaysChaes);
                break;
        }
    }
    return leftDays ?? -1;
}
export const getVacationMinLength = (vacationKind) => {
    const finded = vacationMinLength.find(it => it.type === vacationKind);
    if (finded) {
        return finded.length;
    }
    return 0;
}

export const updateLeftDaysText = (documentData, limitsInfo) => {
    const kindField = documentData.form.fields.find(f => f.name === 'sx:dfDocKind');
    let leftDays = getLimitDays(documentData, limitsInfo);
    if (leftDays >= 0) {
        kindField.labelText = `(${i18next.t('document.leftDaysText')} ${leftDays} ${getWordWithCorrectEnding(leftDays, 'день')})`;
    }
    else {
        kindField.labelText = '';
    }
}

export const getElementByName = (name) => {
    const found = document.getElementsByName(name);
    if (found.length > 0) {
        return found[0];
    }
    return null;
}

export const getCertificationPeriodErrorText = (documentData) => {
    let hasError = false;
    if (documentData.type === alfrescoTypes.certification) {
        const kindVal = documentData.properties['sx:dfDocKind'];
        if (kindVal === certificationKinds.incomeCertificate) {
            if (documentData.properties['sx:ctMonth'] && documentData.properties['sx:ctYearFrom'] && documentData.properties['sx:ctMonthTo'] && documentData.properties['sx:ctYearTo']) {
                const monthFrom = parseInt(documentData.properties['sx:ctMonth'].substring(0, 2));
                const monthTo = parseInt(documentData.properties['sx:ctMonthTo'].substring(0, 2));
                const yearFrom = parseInt(documentData.properties['sx:ctYearFrom']);
                const yearTo = parseInt(documentData.properties['sx:ctYearTo']);
                if (yearFrom > yearTo) {
                    hasError = true;
                }
                else if (yearFrom === yearTo && monthFrom >= monthTo) {
                    hasError = true;
                }
            }
        }
    }
    return hasError ? i18next.t('document.periodError') : '';
}

export const getVacationLengthErrorText = (documentData) => {
    if (documentData.type === alfrescoTypes.vacationClaim) {
        const kindVal = documentData.properties['sx:dfDocKind'];
        const length = documentData.properties['sx:claimeDaysSum'];
        const minLength = getVacationMinLength(kindVal);

        if (length > 0 && length < minLength) {
            return i18next.t('document.vacationLengthError').replace('{0}', minLength);
        }
    }
    return '';
}

// Поле показується на кроці, якщо його NavigationIndex містить поточний крок. Не вказаний
// NavigationIndex означає, що поле видно завжди, коли видно батьківський елемент.
export const isFieldVisibleOnStep = (field, navigationIndex) =>
    navigationIndex === undefined || !field.navigationIndex?.length || field.navigationIndex.includes(navigationIndex);

// Дати документа зберігаються рядками, іноді з часом, тому порівнюємо їх по днях
export const toDayDate = (value) => {
    if (!value) return null;
    const day = value.toString().split('T')[0];
    return day ? new Date(day) : null;
}

// Формат, у якому дати передаються в контроли як межі вибору
export const toDateLimit = (value) => {
    const date = toDayDate(value);
    return date ? moment(date).format('YYYY-MM-DD') : null;
}

// Дати маршруту відрядження зберігаються у вкладеній формі travelForm
export const getRouteDepartureDate = (routeForm) => routeForm?.properties['travelForm']?.[0]?.properties['sx:tfDepartureDate'];
export const getRouteArrivalDate = (routeForm) => routeForm?.properties['travelForm']?.[0]?.properties['sx:tfArrivalDate'];

// Вікно, в яке має вкладатись проживання маршруту. Кілька маршрутів: від виїзду цього
// маршруту до виїзду наступного, а для останнього — до дати «Назад» з пункту «Повернення»
// (його виїзд; дата приїзду є лише запасною, бо виставляється кінцем відрядження лише
// після збереження). Один маршрут: до приїзду назад, якщо транспорт назад той самий,
// інакше — до виїзду назад.
export const getResidenceDateRange = (routeForms, index, goingBackForms, multipleRoutes) => {
    const routeForm = routeForms?.[index];
    if (!routeForm) return {};
    const from = getRouteDepartureDate(routeForm);
    const to = multipleRoutes
        ? getRouteDepartureDate(routeForms[index + 1]) || getRouteDepartureDate(goingBackForms?.[0]) || getRouteArrivalDate(goingBackForms?.[0])
        : (routeForm.properties['sx:pbtSameTransportBack']
            ? getRouteArrivalDate(routeForm)
            : routeForm.properties['dateStartBack']);
    return { from, to };
}

// Порожня дата обмеженням не суперечить, тому скидати її не треба
export const isDateOutOfRange = (value, minValue, maxValue) => {
    const date = toDayDate(value);
    if (!date) return false;
    const minDate = toDayDate(minValue);
    const maxDate = toDayDate(maxValue);
    return (minDate && date < minDate) || (maxDate && date > maxDate);
}

// Такі поля редагують одразу кілька властивостей документа, тому отримують значення всіх
// полів, перелічених у їх Settings, замість значення власного поля. Імена полів документа
// зберігаються в ключах Settings, які закінчуються на "Field", решта налаштувань (заголовки
// тощо) використовуються самим контролом.
export const getFieldValue = (field, properties) => {
    if (settingsValueFieldTypes.includes(field.type)) {
        const value = {};
        Object.keys(field.settings ?? {})
            .filter(key => key.toLowerCase().endsWith('field'))
            .forEach(key => value[field.settings[key]] = properties[field.settings[key]]);
        return value;
    }
    return properties[field.name];
}

// Значення таких полів розкладається по властивостях документа, які в ньому перелічені
export const setFieldValue = (properties, newValue) => {
    Object.keys(newValue ?? {}).forEach(fieldName => properties[fieldName] = newValue[fieldName]);
}

// Поля з варіантами вибору отримують перший варіант як значення за замовчуванням.
// HasEmptyValue означає, що поле може лишатись незаповненим, тому значення не підставляється.
export const initSelectFieldsValues = (fields, properties, keepFilledValues = false) => {
    fields.filter(f => f.type === formFieldTypes.select || f.type === formFieldTypes.radioButtonGroup || f.type === formFieldTypes.transportSelect)
        .forEach(field => {
            if (keepFilledValues && properties[field.name]) return;
            if (field.values?.data?.length > 0) {
                properties[field.name] = field.hasEmptyValue ? '' : field.values.data[0].value;
            }
        });
}

export const findField = (fields, name) => {
    for (let i = 0; i < fields.length; i++) {
        if (fields[i].name === name) {
            return fields[i];
        }
        if (fields[i].type === formFieldTypes.group || fields[i].type === formFieldTypes.subform
            || fields[i].type === formFieldTypes.formGroup) {
            const result = findField(fields[i].fields, name);
            if (result) {
                return result;
            }
        }
    }
    return null;
}

export function viewOrder(businessTripNodeRef) {
    if (businessTripNodeRef) {
        const id = businessTripNodeRef.split('/').pop();
        fetch(`/api/document/GetMainFileNodeRef/${id}`)
            .then(response => response.text())
            .then(nodeRef => {
                if (nodeRef) {
                    viewDocumentFile(nodeRef);
                }
                else {
                    messageDialog(i18next.t('error'), i18next.t("viewedDocumentNotFound"));
                }
            })
            .catch(() => {
                messageDialog(i18next.t('error'), i18next.t("viewedDocumentNotFound"));
            });
    }
}

// Файл, який користувач щойно вибрав, читається через FileReader.readAsDataURL,
// тож копія збереженого файлу має бути в такому ж вигляді — з data-URL у content.
// Бекенд при збереженні теж чекає саме такий вміст: AlfrescoFileManager відрізає
// у нього префікс ";base64,"
function getBase64Size(base64) {
    const padding = base64.endsWith('==') ? 2 : base64.endsWith('=') ? 1 : 0;
    return Math.floor(base64.length * 3 / 4) - padding;
}

// у списку вкладень збережений файл представлений лише nodeRef, тому його вміст
// доводиться окремо викачувати з сервера. Тут не використовується getDodumentFiles,
// бо той підміняє вміст на blob-посилання, непридатне для повторного збереження
async function loadAttachmentCopy(attachment) {
    const response = await fetch('/api/file/GetAttachedFiles', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify([{ nodeRef: attachment.nodeRef }])
    });
    if (!response.ok) {
        return null;
    }
    const data = await response.json();
    const file = data?.[0]?.file;
    if (!file?.content) {
        return null;
    }
    const mimeType = file.mimeType ?? attachment.value?.mimeType;
    // копія свідомо йде без nodeRef: тільки такі вкладення відправляються на сервер
    // при збереженні документа, файл з nodeRef вважається вже збереженим
    return {
        type: attachmentTypes.file,
        value: {
            fileName: file.fileName ?? attachment.value?.fileName,
            mimeType: mimeType,
            size: getBase64Size(file.content),
            content: `data:${mimeType};base64,${file.content}`
        }
    };
}

// Повертає true, якщо список поповнився: копії викачуються асинхронно, тому
// виклик має після цього перемалювати форму
export async function updateCarInfoFiles(documentData, attachments) {
    const filledCarAttachments = findFilledOwnCarAttachments(documentData);
    if (!filledCarAttachments) {
        return false;
    }
    // копії вантажаться паралельно, а Promise.all зберігає початковий порядок файлів
    const copies = await Promise.all(filledCarAttachments.map(attachment =>
        attachment.nodeRef ? loadAttachmentCopy(attachment) : attachment));
    // файл, який не вдалося викачати, просто пропускається — решту це не блокує
    const addedCopies = copies.filter(copy => copy);
    attachments.push(...addedCopies);
    return addedCopies.length > 0;
}

function findFilledOwnCarAttachments(documentData) {
    if (documentData.properties['sx:btBusinessTripDestination'] === tripDestinations.ukraine) {
        if (documentData.properties['sx:btMultipleRoutes']) {
            if (documentData.properties['sx:btSameTransportForRoutes']) {
                if (documentData.properties['methodTransportationForAll'] === transportationMethods.ownCar) {
                    const carInfoForm = documentData.properties['carInfoForm_forAll']?.[0];
                    if (carInfoForm?.properties['ownCarAttachments']?.length > 0) {
                        return carInfoForm.properties['ownCarAttachments'];
                    }
                }
            }
            const forms = [];
            if (documentData.properties['routeForm']?.length > 0) {
                forms.push(...documentData.properties['routeForm'])
            }
            if (documentData.properties['goingBackForm']?.length > 0) {
                forms.push(documentData.properties['goingBackForm'][0])
            }
            for (let i = 0; i < forms.length; i++) {
                const routeForm = forms[i];
                if (routeForm.properties['sx:pbtMethodTransportation'] === transportationMethods.ownCar) {
                    const carInfoForm = routeForm.properties['carInfoForm']?.[0];
                    if (carInfoForm?.properties['ownCarAttachments']?.length > 0) {
                        return carInfoForm.properties['ownCarAttachments'];
                    }
                }
            }
        }
        else {
            const routeForm = documentData.properties['routeForm']?.[0];
            if (routeForm?.properties['sx:pbtMethodTransportation'] === transportationMethods.ownCar) {
                const carInfoForm = routeForm.properties['carInfoForm']?.[0];
                if (carInfoForm?.properties['ownCarAttachments']?.length > 0) {
                    return carInfoForm.properties['ownCarAttachments'];
                }
                if (!routeForm.properties['sx:pbtSameTransportBack']) {
                    if (routeForm.properties['sx:pbtBackMethodTransportation'] === transportationMethods.ownCar) {
                        const backCarInfoForm = routeForm.properties['carInfoBackForm']?.[0];
                        if (backCarInfoForm?.properties['ownCarAttachments']?.length > 0) {
                            return backCarInfoForm.properties['ownCarAttachments'];
                        }
                    }
                }
            }
        }
    }
    else {
        const routeForm = documentData.properties['routeForm']?.[0];
        if (routeForm?.properties['sx:pbtMethodTransportation'] === transportationMethods.ownCar) {
            const carInfoForm = routeForm.properties['carInfoForm']?.[0];
            if (carInfoForm?.properties['ownCarAttachments']?.length > 0) {
                return carInfoForm.properties['ownCarAttachments'];
            }
        }
        if (routeForm) {
            if (!routeForm.properties['sx:pbtSameTransportBack']) {
                if (routeForm.properties['sx:pbtBackMethodTransportation'] === transportationMethods.ownCar) {
                    const backCarInfoForm = routeForm.properties['carInfoBackForm']?.[0];
                    if (backCarInfoForm?.properties['ownCarAttachments']?.length > 0) {
                        return backCarInfoForm.properties['ownCarAttachments'];
                    }
                }
            }
            if (routeForm.properties['transitForm']?.length > 0) {
                for (let i = 0; i < routeForm.properties['transitForm'].length; i++) {
                    const transitForm = routeForm.properties['transitForm'][i];
                    if (transitForm.properties['sx:pbtMethodTransportation'] === transportationMethods.ownCar) {
                        const transitCarInfoForm = transitForm.properties['carInfoForm']?.[0];
                        if (transitCarInfoForm?.properties['ownCarAttachments']?.length > 0) {
                            return transitCarInfoForm.properties['ownCarAttachments'];
                        }
                    }
                }
            }
        }
    }
    return null;
}